import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Search, Download, AlertCircle, ExternalLink } from 'lucide-react';
import { leadsListService } from '../lib/supabaseServices';

const LeadsListViewer = ({ isOpen, onClose, darkMode, leadsList }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load leads when modal opens
  useEffect(() => {
    if (isOpen && leadsList) {
      loadLeads();
    }
  }, [isOpen, leadsList]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const leadsData = await leadsListService.getLeadsFromList(leadsList.id, 1000);
      setLeads(leadsData);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.instagram_username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.display_name && lead.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleExportLeads = () => {
    const csvContent = [
      ['Instagram Username', 'Display Name'],
      ...filteredLeads.map(lead => [lead.instagram_username, lead.display_name || ''])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${leadsList.name.replace(/[^a-z0-9]/gi, '_')}_leads.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    onClose();
    // Reset state after animation
    setTimeout(() => {
      setLeads([]);
      setSearchTerm('');
      setError(null);
    }, 200);
  };

  if (!isOpen || !leadsList) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`${
            darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'
          } rounded shadow-2xl border max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-b flex items-center justify-between flex-shrink-0`}>
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-accent to-accent-hover'} rounded flex items-center justify-center`}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {leadsList.name}
                </h2>
                <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                  {leadsList.total_leads?.toLocaleString() || 0} leads total
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {leads.length > 0 && (
                <button
                  onClick={handleExportLeads}
                  className={`p-2 ${darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded transition-colors`}
                  title="Export to CSV"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={handleClose}
                className={`p-2 ${darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded transition-colors`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-3 border-b flex-shrink-0`}>
            <div className="relative">
              <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 pr-4 py-2 w-full border rounded focus:ring-2 focus:border-transparent text-sm ${
                  darkMode 
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
                }`}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-4 p-3 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className={`${darkMode ? 'bg-slate-800' : 'bg-gray-100'} rounded p-4 animate-pulse`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} rounded-full`}></div>
                      <div className="flex-1">
                        <div className={`w-32 h-4 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} rounded mb-1`}></div>
                        <div className={`w-24 h-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} rounded`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium mb-1">
                  {searchTerm ? 'No leads found' : 'No leads available'}
                </p>
                <p className="text-sm">
                  {searchTerm 
                    ? 'Try adjusting your search terms' 
                    : 'This leads list appears to be empty'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
                  Showing {filteredLeads.length} of {leads.length} leads
                </div>
                {filteredLeads.map((lead, index) => (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={`${
                      darkMode ? 'bg-surface hover:bg-elevated' : 'bg-gray-50 hover:bg-gray-100'
                    } rounded p-4 transition-colors group`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-accent to-accent-hover'} rounded-full flex items-center justify-center`}>
                          <span className="text-white font-semibold text-sm">
                            {lead.instagram_username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            @{lead.instagram_username}
                          </div>
                          {lead.display_name && (
                            <div className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
                              {lead.display_name}
                            </div>
                          )}
                        </div>
                      </div>
                      <a
                        href={`https://instagram.com/${lead.instagram_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-2 ${darkMode ? 'text-secondary hover:text-accent' : 'text-gray-400 hover:text-accent'} rounded transition-colors opacity-0 group-hover:opacity-100`}
                        title="View Instagram Profile"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadsListViewer;